import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { http } from "@/lib/api";
import i18n from "@/i18n";
import { normaliseLanguage } from "@/i18n/languages";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await http.get("/auth/me");
      // A language the person chose on their account follows them to every device.
      // Apply it before the app renders, so the first requests (daily quote, Spicy
      // fact) are already in the right language and the AI isn't asked twice.
      if (data?.language) await i18n.changeLanguage(normaliseLanguage(data.language));
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: returning from OAuth — AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await http.post("/auth/logout");
    } catch {}
    setUser(false);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
